import { Component, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TriStateCheckboxModule } from 'primeng/tristatecheckbox';
import { PanelMenuModule } from 'primeng/panelmenu';
import { DividerModule } from 'primeng/divider';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  BulkActionStore,
  BulkEntityTypes,
  routeActionMapping,
  routeEntityTypeMapping,
} from '../../../stores/bulk.state';
import { getState, patchState } from '@ngrx/signals';
import { RoutingService } from '../../../services/routing.service';
import { filter, startWith, Subscription } from 'rxjs';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { BulkActionStepperComponent } from '../../molecules/bulk-action-stepper/bulk-action-stepper.component';
import { NgIf } from '@angular/common';
import { CategoryStore } from '../../../stores/category/category.state';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';

@Component({
  selector: 'app-bulk-actions',
  standalone: true,
  imports: [
    ButtonModule,
    TriStateCheckboxModule,
    PanelMenuModule,
    DividerModule,
    BulkActionStepperComponent,
    NgIf,
  ],
  templateUrl: './bulk-actions.component.html',
  styleUrl: './bulk-actions.component.scss',
})
export class BulkActionsComponent implements OnInit, OnDestroy {
  readonly bulkStore = inject(BulkActionStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly dataStore = inject(IndicatorDataStore);
  private routerSubscription: Subscription = new Subscription();

  initialized = false;
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-12',
    icon: 'fas fa-list-check',
    headerText: 'Sammelaktionen',
    subtitle:
      'Bearbeiten Sie mehrere Indikatoren, Indikatorenordner und Daten gleichzeitig – inklusive Veröffentlichen, Verschieben und Verwalten.',
  };

  actions: { [key: string]: MenuItem } = {
    remove: {
      label: 'löschen',
      icon: 'fas fa-trash',
    },
    move: {
      label: 'verschieben',
      icon: 'fas fa-arrow-down-up-across-line',
    },
    lock: {
      label: 'sperren',
      icon: 'fas fa-lock',
    },
    unlock: {
      label: 'freigeben',
      icon: 'fas fa-check',
    },
    publish: {
      label: 'veröffentlichen',
      icon: 'fas fa-earth-europe',
    },
    unpublish: {
      label: 'intern schalten',
      icon: 'fas fa-house-user',
    },
    addTags: {
      label: 'Schlagworte zuweisen',
      icon: 'fas fa-tags',
    },
    addToSet: {
      label: 'Indikatorenset zuordnen',
      icon: 'fas fa-ruler-combined',
    },
  };

  entities: { [key: string]: MenuItem } = {};
  actionMenu: MenuItem[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private routingService: RoutingService,
    private sidebarService: SidebarService
  ) {
    effect(() => {
      // return back to mass-action-root
      if (this.bulkStore.currentStep() === '') {
        this.routingService.navigateBack();
      }

      const indicatorState = getState(this.indicatorStore);
      const categoryState = getState(this.categoryStore);
      const dataState = getState(this.dataStore);

      if (
        !this.initialized &&
        indicatorState.initialized &&
        categoryState.initialized &&
        dataState.initialized
      ) {
        this.initMenu();
      }
    });
  }

  /**
   * Retrieves the entity type based on the given route.
   *
   * @param {string} entityType - The route or key representing the entity type.
   * @return {BulkEntityTypes} The corresponding entity type for the provided route.
   */
  getEntityTypeByRoute(entityType: string): BulkEntityTypes {
    return routeEntityTypeMapping[entityType];
  }

  /**
   * Retrieves the corresponding action handler based on the given route action.
   *
   * @param {string} action - The action associated with a specific route.
   * @return {string} The action handler mapped to the given route action.
   */
  getActionByRoute(action: string): string {
    return routeActionMapping[action];
  }

  /**
   * Initializes the component by setting up necessary configurations
   * and subscribing to route change events to manage state updates.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    this.sidebarService.setConfiguration(this.sidebarConfig);
    this.routerSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(this.router.url) // Ensure initial load is handled
      )
      .subscribe(() => {
        this.routingService
          .getDeepestChild(this.route)
          ?.params.subscribe(params => {
            const entity = this.getEntityTypeByRoute(params['entity']);
            const action = this.getActionByRoute(params['action']);
            patchState(this.bulkStore, {
              selectedEntityType: entity,
              selectedAction: action,
            });
            patchState(this.bulkStore, {
              currentStep: this.bulkStore.getSteps()?.[0] ?? [],
            });
          });
      });
  }

  /**
   * Initializes the menu with predefined entities and their associated actions.
   * The menu consists of various categories (Indicator, Category, IndicatorData),
   * each containing action items that are dynamically enabled or disabled
   * based on the state of their respective data stores.
   *
   * @return {void} This method does not return a value but initializes the menu system in the component.
   */
  initMenu(): void {
    this.entities = {
      Indicator: {
        label: 'Indikatoren',
        icon: 'fas fa-ruler-vertical',
        disabled: this.indicatorStore.asArray().length === 0,
        items: [
          this.createActionMenuItem(
            this.actions['remove'],
            'indikatoren',
            'löschen'
          ),
          this.createActionMenuItem(
            this.actions['move'],
            'indikatoren',
            'verschieben'
          ),
          this.createActionMenuItem(
            this.actions['lock'],
            'indikatoren',
            'sperren'
          ),
          this.createActionMenuItem(
            this.actions['unlock'],
            'indikatoren',
            'freigeben'
          ),
          this.createActionMenuItem(
            this.actions['addTags'],
            'indikatoren',
            'verschlagworten'
          ),
          this.createActionMenuItem(
            this.actions['addToSet'],
            'indikatoren',
            'versetten'
          ),
        ],
      },
      Category: {
        label: 'Indikatorenordner',
        icon: 'fas fa-folder',
        disabled: this.categoryStore.asArray().length === 0,
        items: [
          this.createActionMenuItem(
            this.actions['remove'],
            'bereiche',
            'löschen'
          ),
          this.createActionMenuItem(
            this.actions['move'],
            'bereiche',
            'verschieben'
          ),
          this.createActionMenuItem(
            this.actions['lock'],
            'bereiche',
            'sperren'
          ),
          this.createActionMenuItem(
            this.actions['unlock'],
            'bereiche',
            'freigeben'
          ),
        ],
      },
      IndicatorData: {
        label: 'Daten',
        icon: 'fas fa-database',
        disabled: this.dataStore.asArray().length === 0,
        items: [
          this.createActionMenuItem(this.actions['remove'], 'daten', 'löschen'),
          this.createActionMenuItem(
            this.actions['unpublish'],
            'daten',
            'intern schalten'
          ),
          this.createActionMenuItem(
            this.actions['publish'],
            'daten',
            'veröffentlichen'
          ),
        ],
      },
    };

    this.actionMenu = Object.values(this.entities);
    this.initialized = true;
  }

  /**
   * Retrieves a menu item associated with the specified key.
   *
   * @param {string} key - The key used to identify the menu item.
   * @return {MenuItem} The menu item corresponding to the given key.
   */
  getActionByKey(key: string): MenuItem {
    return this.actions[key];
  }

  /**
   * Retrieves the entity type associated with the given key.
   *
   * @param {string} key - The unique identifier for the desired entity.
   * @return {MenuItem} The entity type corresponding to the specified key.
   */
  getEntityTypeByKey(key: string): MenuItem {
    return this.entities[key];
  }

  /**
   * Creates a new menu item with a router link configured using the provided entity and route.
   *
   * @param {MenuItem} action - The base action menu item to modify.
   * @param {string} entity - The entity name to include in the router link.
   * @param {string} route - The route to include in the router link.
   * @return {MenuItem} A new menu item with the router link configured.
   */
  createActionMenuItem(
    action: MenuItem,
    entity: string,
    route: string
  ): MenuItem {
    return {
      ...action,
      routerLink: [{ outlets: { aktion: [entity, route] } }],
    };
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * This method is automatically called when Angular determines that the component is no longer needed.
   * It handles cleanup operations such as resetting state and unsubscribing from observable subscriptions
   * to prevent memory leaks.
   *
   * In this implementation:
   * - Resets the state of the bulk store.
   * - Unsubscribes from the router subscription.
   *
   * @return {void} This method does not return anything.
   */
  ngOnDestroy(): void {
    this.bulkStore.resetState();
    this.routerSubscription.unsubscribe();
  }
}
